(() => {
  'use strict';

  const GLOBAL_FLAG = '__orderProgressGlobalBound';
  const ROOT_SELECTOR = '.order-progress-page';
  const PROGRESS_ENDPOINT = '/v1/public/ptw/order_progress';
  const FEEDBACK_ENDPOINT = '/v1/public/ptw/candidate_image_feedback';
  const PHOTO_SELECTION_ENDPOINT = '/v1/public/ptw/photo_selection';
  const PHOTO_CONFIRMATION_STATUS = 'awaiting_photo_confirmation';
  const PLATE_MAKING_STATUS = 'awaiting_plate_making';
  const ACTIONABLE_STATUS = 'awaiting_candidate_image_confirmation';
  const WAITING_STATUS = 'created';
  const LOADING_PREVIEW_STATUS = 'loading';
  const DEFAULT_VISIBLE_STATUSES = new Set([
    WAITING_STATUS,
    PHOTO_CONFIRMATION_STATUS,
    PLATE_MAKING_STATUS,
    ACTIONABLE_STATUS,
    'awaiting_production',
    'awaiting_optimization',
    'ready_to_ship',
    'shipped',
    'delivered'
  ]);
  const EDITOR_PREVIEW_MESSAGES = {
    noToken: 'Previewing editor content because no token was provided.',
    submissionDisabled: 'Submission is disabled while previewing in the theme editor.'
  };
  const SUCCESS_DIALOG_CONTENT = {
    confirm_photos: {
      title: 'Photos confirmed',
      body: 'Thanks for confirming your pet photos. Our team will now begin preparing your embroidery design.'
    },
    approve: {
      title: 'Design approved',
      body: 'Thanks for reviewing your embroidery design. We’ve received your approval and will move your order into production.'
    },
    request_edits: {
      title: 'Revision request received',
      body: 'Thanks for your feedback. Our designer will review your notes and prepare an updated preview for you.'
    }
  };
  const VALIDATION_DIALOG_CONTENT = {
    request_edits: {
      title: 'Add your revision notes',
      body: 'Please let us know what you’d like us to adjust before submitting your revision request.'
    }
  };
  const APPROVE_CONFIRM_DIALOG_CONTENT = {
    title: 'Ready to approve?',
    body: 'Take one last look and make sure it feels just right. Once approved, we’ll begin stitching your pet’s story into your piece, and changes can no longer be made after production begins.'
  };
  const REVISION_NOTE_STORAGE_PREFIX = 'order-progress-revision-note:';

  function resolveEndpoint(endpoint) {
    if (typeof endpoint !== 'string') {
      throw new Error('Endpoint must be a non-empty string.');
    }

    const trimmed = endpoint.trim();
    const isAbsoluteUrl = /^https?:\/\//i.test(trimmed);
    const isRelativePath = trimmed.startsWith('/');

    if (!trimmed || (!isAbsoluteUrl && !isRelativePath)) {
      throw new Error('Endpoint must be a complete path starting with "/" or an absolute URL.');
    }

    return trimmed;
  }

  function getTokenFromContext(root) {
    const queryToken = new URLSearchParams(window.location.search).get('token');
    return root.dataset.token || queryToken || '';
  }

  function isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  function buildRequestUrl(baseUrl, root, options) {
    if (!options || !options.endpoint) {
      throw new Error('Endpoint is required. Pass { endpoint: "/your/path" } when calling fetchOrderProgress.');
    }

    const endpoint = resolveEndpoint(options.endpoint);
    const url = new URL(endpoint, baseUrl || window.location.origin);
    const token = getTokenFromContext(root);

    if (token) {
      url.searchParams.set('token', token);
    }

    if (options.query && typeof options.query === 'object') {
      Object.entries(options.query).forEach(([key, value]) => {
        if (value != null && value !== '') {
          url.searchParams.set(key, String(value));
        }
      });
    }

    return url.toString();
  }

  async function fetchJson(root, options = {}) {
    const baseUrl = root.dataset.apiBaseUrl;
    if (!baseUrl) {
      return {
        success: false,
        data: null,
        error: 'Missing data-api-base-url.'
      };
    }

    const requestUrl = buildRequestUrl(baseUrl, root, options);

    try {
      const response = await fetch(requestUrl, {
        method: options.method || 'GET',
        headers: options.headers || { Accept: 'application/json' },
        credentials: 'same-origin',
        body: options.body
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        const message = data && data.error && data.error.message
          ? String(data.error.message)
          : `Request failed: ${response.status}`;
        throw new Error(message);
      }

      return {
        success: true,
        data,
        error: null
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        error: error instanceof Error ? error.message : String(error || 'Unknown error')
      };
    }
  }

  function fetchOrderProgress(root) {
    return fetchJson(root, { endpoint: PROGRESS_ENDPOINT });
  }

  function submitPhotoSelectionRequest(root, body) {
    return fetchJson(root, {
      endpoint: PHOTO_SELECTION_ENDPOINT,
      method: 'POST',
      headers: { Accept: 'application/json' },
      body
    });
  }

  function getPayloadData(payload) {
    return payload && payload.data && typeof payload.data === 'object' ? payload.data : payload;
  }

  function setLoading(root, isLoading) {
    root.dataset.loading = isLoading ? 'true' : 'false';
  }

  function setSubmitLoading(root, isLoading) {
    root.dataset.submitting = isLoading ? 'true' : 'false';
  }

  function getStorageKey(root) {
    const token = getTokenFromContext(root);
    if (!token) {
      return '';
    }

    return `${REVISION_NOTE_STORAGE_PREFIX}${token}`;
  }

  function persistRevisionNote(root, note) {
    const storageKey = getStorageKey(root);
    if (!storageKey) {
      return;
    }

    try {
      if (note) {
        window.localStorage.setItem(storageKey, note);
      } else {
        window.localStorage.removeItem(storageKey);
      }
    } catch (_error) {
      // Ignore storage failures and continue rendering without persisted notes.
    }
  }

  function readPersistedRevisionNote(root) {
    const storageKey = getStorageKey(root);
    if (!storageKey) {
      return '';
    }

    try {
      return window.localStorage.getItem(storageKey) || '';
    } catch (_error) {
      return '';
    }
  }

  function setVisibility(root, isVisible) {
    const shell = root.querySelector('[data-order-progress-shell]');
    if (!shell) {
      return;
    }

    if (isVisible) {
      shell.hidden = false;
      root.hidden = false;
      return;
    }

    shell.hidden = true;
    root.hidden = true;
  }

  function showLoadingPreview(root) {
    const shell = root.querySelector('[data-order-progress-shell]');
    root.hidden = false;
    root.dataset.loading = 'true';
    root.dataset.submitting = 'false';
    delete root.dataset.currentStatus;
    root.__orderProgressPreviewMode = true;

    if (shell) {
      shell.hidden = true;
      shell.style.minHeight = '';
    }
  }

  function setStatusMessage(root, message, tone) {
    const statusNode = root.querySelector('[data-order-progress-status]');
    if (!statusNode) {
      return;
    }

    statusNode.textContent = message || '';
    if (tone) {
      statusNode.dataset.tone = tone;
      return;
    }

    delete statusNode.dataset.tone;
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function parseStatusConfigs(root) {
    const script = root.querySelector('[data-order-progress-configs]');
    if (!script) {
      return [];
    }

    try {
      const parsed = JSON.parse(script.textContent || '[]');
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed
        .filter((config) => config && typeof config === 'object' && typeof config.status === 'string')
        .map((config) => ({
          id: typeof config.id === 'string' ? config.id : '',
          status: config.status.trim(),
          backgroundColor: typeof config.background_color === 'string' ? config.background_color.trim() : '',
          title: typeof config.title === 'string' ? config.title : '',
          step: Number(config.step || 0),
          shellMinHeight: Number(config.shell_min_height || 0),
          titleFontSize: Number(config.title_font_size || 0),
          bodyFontSize: Number(config.body_font_size || 0),
          previewImageUrl: typeof config.preview_image_url === 'string' ? config.preview_image_url : '',
          bodyHtml: typeof config.body_html === 'string' ? config.body_html : '',
          designerNoteLabel: typeof config.designer_note_label === 'string' ? config.designer_note_label : '',
          showDesignerNote: Boolean(config.show_designer_note),
          designerNoteContent: typeof config.designer_note_content === 'string' ? config.designer_note_content : '',
          showFeedbackForm: Boolean(config.show_feedback_form),
          showRevisionFeedback: Boolean(config.show_revision_feedback),
          previewJourneyJson: typeof config.preview_journey_json === 'string' ? config.preview_journey_json : '',
          feedbackLabel: typeof config.feedback_label === 'string' ? config.feedback_label : '',
          feedbackPlaceholder: typeof config.feedback_placeholder === 'string' ? config.feedback_placeholder : '',
          uploadTriggerLabel: typeof config.upload_trigger_label === 'string' ? config.upload_trigger_label : '',
          finePrint: typeof config.fine_print === 'string' ? config.fine_print : '',
          primaryButtonLabel: typeof config.primary_button_label === 'string' ? config.primary_button_label : '',
          secondaryButtonLabel: typeof config.secondary_button_label === 'string' ? config.secondary_button_label : ''
        }));
    } catch (_error) {
      return [];
    }
  }

  function getStatusConfigs(root) {
    if (!Array.isArray(root.__orderProgressConfigs)) {
      root.__orderProgressConfigs = parseStatusConfigs(root);
    }

    return root.__orderProgressConfigs;
  }

  function getStatusConfig(root, status) {
    if (typeof status !== 'string' || !status.trim()) {
      return null;
    }

    return getStatusConfigs(root).find((config) => config.status === status.trim()) || null;
  }

  function getDefaultConfig(root) {
    const configs = getStatusConfigs(root);
    if (!configs.length) {
      return null;
    }

    const previewStatus = typeof root.dataset.previewStatus === 'string' ? root.dataset.previewStatus.trim() : '';
    if (previewStatus) {
      return getStatusConfig(root, previewStatus) || configs[0];
    }

    return getStatusConfig(root, ACTIONABLE_STATUS) || configs[0];
  }

  function getVisibleStatuses(root) {
    const statuses = new Set(DEFAULT_VISIBLE_STATUSES);
    getStatusConfigs(root).forEach((config) => {
      if (config.status) {
        statuses.add(config.status);
      }
    });
    return statuses;
  }

  function updateTitle(root, config) {
    const titleNode = root.querySelector('[data-order-progress-title]');
    if (!titleNode) {
      return;
    }

    const fallbackTitle = titleNode.textContent || '';
    const nextTitle = config && config.title ? config.title : fallbackTitle;
    titleNode.textContent = nextTitle;
  }

  function updateSteps(root, config) {
    const currentStep = config && Number(config.step || 0);
    root.querySelectorAll('[data-step-index]').forEach((stepNode) => {
      const stepIndex = Number(stepNode.dataset.stepIndex || '0');
      stepNode.classList.toggle('order-progress-page__step--active', currentStep > 0 && stepIndex === currentStep);
    });
  }

  function updateShellMinHeight(root, config) {
    const shell = root.querySelector('[data-order-progress-shell]');
    if (!shell) {
      return;
    }

    const minHeight = config && Number(config.shellMinHeight || 0);
    shell.style.minHeight = minHeight > 0 ? `${minHeight}px` : '';
  }

  function updateTypography(root, config) {
    const titleFontSize = config && Number(config.titleFontSize || 0);
    const bodyFontSize = config && Number(config.bodyFontSize || 0);

    root.style.setProperty('--order-progress-title-font-size', titleFontSize > 0 ? `${titleFontSize}px` : '');
    root.style.setProperty('--order-progress-body-font-size', bodyFontSize > 0 ? `${bodyFontSize}px` : '');
  }

  function updateBackgroundColor(root, config) {
    const backgroundColor = config && typeof config.backgroundColor === 'string'
      ? config.backgroundColor.trim()
      : '';

    root.style.setProperty('--order-progress-background-color', backgroundColor || '');
  }

  function updateDesignNotes(root, status, data, config, previewMode) {
    const noteBlock = root.querySelector('[data-order-progress-design-note-block]');
    const noteLabelNode = root.querySelector('[data-order-progress-design-note-label]');
    const notesNode = root.querySelector('[data-order-progress-design-notes]');
    if (!noteBlock || !noteLabelNode || !notesNode) {
      return;
    }

    const apiNote = data && typeof data.design_notes === 'string' ? data.design_notes.trim() : '';
    const configLabel = config && typeof config.designerNoteLabel === 'string' ? config.designerNoteLabel.trim() : '';
    const configNote = config && typeof config.designerNoteContent === 'string' ? config.designerNoteContent.trim() : '';
    const shouldShowConfiguredNote = Boolean(previewMode && config && config.showDesignerNote && configNote);
    const nextValue = previewMode ? (apiNote || configNote) : apiNote;
    const shouldShowNote = status === ACTIONABLE_STATUS && Boolean(configLabel) && Boolean(previewMode ? (apiNote || shouldShowConfiguredNote) : apiNote);

    noteBlock.hidden = !shouldShowNote;
    noteLabelNode.textContent = configLabel;
    notesNode.textContent = shouldShowNote ? nextValue : '';
  }

  function resolveRevisionNote(data, root) {
    const candidates = [
      data && data.feedback_text,
      data && data.comment,
      data && data.notes,
      data && data.feedback_comment,
      data && data.latest_comment,
      data && data.latest_notes,
      data && data.candidate_image_feedback_comment,
      data && data.latest_feedback_comment,
      data && data.feedback && data.feedback.comment,
      data && data.latest_feedback && data.latest_feedback.comment
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }

    return readPersistedRevisionNote(root).trim();
  }

  function updateRevisionSummary(root, config, data, previewMode) {
    const summary = root.querySelector('[data-order-progress-revision-summary]');
    const notesWrapper = root.querySelector('[data-order-progress-revision-notes]');
    const notesText = root.querySelector('[data-order-progress-revision-notes-text]');
    const feedbackImagesWrapper = root.querySelector('[data-order-progress-revision-feedback-images]');
    const feedbackImagesGrid = root.querySelector('[data-order-progress-revision-feedback-grid]');
    if (!summary || !notesWrapper || !notesText || !feedbackImagesWrapper || !feedbackImagesGrid) {
      return;
    }

    const shouldShowSummary = Boolean(config && config.showRevisionFeedback);
    summary.hidden = !shouldShowSummary;

    if (!shouldShowSummary) {
      notesWrapper.hidden = true;
      notesText.textContent = '';
      feedbackImagesWrapper.hidden = true;
      feedbackImagesGrid.innerHTML = '';
      return;
    }

    const note = resolveRevisionNote(data, root);
    const previewNote = previewMode ? 'Your requested changes will be shown here in the editor preview.' : '';
    const displayNote = note || previewNote;
    notesWrapper.hidden = !displayNote;
    notesText.textContent = displayNote;

    const feedbackImages = Array.isArray(data && data.feedback_images)
      ? data.feedback_images.filter((imageUrl) => typeof imageUrl === 'string' && imageUrl.trim())
      : [];

    feedbackImagesWrapper.hidden = !feedbackImages.length;
    feedbackImagesGrid.innerHTML = feedbackImages.map((imageUrl, index) => {
      const escapedUrl = imageUrl.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
      const alt = `Feedback image ${index + 1}`;

      return `
        <div class="order-progress-page__revision-feedback-item">
          <img src="${escapedUrl}" alt="${alt}" loading="lazy">
        </div>
      `;
    }).join('');
  }

  function renderBodyContent(root, config) {
    const bodyContainer = root.querySelector('[data-order-progress-body-content]');
    if (!bodyContainer) {
      return;
    }

    const bodyHtml = config && typeof config.bodyHtml === 'string' ? config.bodyHtml.trim() : '';
    bodyContainer.innerHTML = bodyHtml;
  }

  function getPreviewJourneyItems(config) {
    const raw = config && typeof config.previewJourneyJson === 'string'
      ? config.previewJourneyJson.trim()
      : '';

    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.filter((item) => item && typeof item === 'object')
        : [];
    } catch (_error) {
      return [];
    }
  }

  function renderJourney(root, status, data, config, previewMode) {
    const section = root.querySelector('[data-order-progress-journey]');
    const list = root.querySelector('[data-order-progress-journey-list]');
    if (!section || !list) {
      return;
    }

    const shouldShow = status === 'shipped' || status === 'delivered';
    if (!shouldShow) {
      section.hidden = true;
      list.innerHTML = '';
      return;
    }

    const previewJourneyItems = previewMode ? getPreviewJourneyItems(config) : [];
    const journeyItems = Array.isArray(data && data.journey)
      ? data.journey.filter((item) => item && typeof item === 'object')
      : previewJourneyItems;

    if (!journeyItems.length) {
      section.hidden = true;
      list.innerHTML = '';
      return;
    }

    section.hidden = false;
    list.innerHTML = journeyItems.map((item, itemIndex) => {
      const imageUrls = Array.isArray(item.images)
        ? item.images.filter((imageUrl) => typeof imageUrl === 'string' && imageUrl.trim())
        : [];
      const date = typeof item.date === 'string' ? item.date.trim() : '';
      const title = typeof item.title === 'string' ? item.title.trim() : '';
      const imageMarkup = imageUrls.map((imageUrl, imageIndex) => {
        const escapedUrl = imageUrl.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
        const alt = title || `Journey image ${itemIndex + 1}.${imageIndex + 1}`;
        const escapedAlt = escapeHtml(alt);

        return `
          <div class="order-progress-page__journey-image">
            <img src="${escapedUrl}" alt="${escapedAlt}" loading="lazy">
          </div>
        `;
      }).join('');

      return `
        <article class="order-progress-page__journey-item">
          <div class="order-progress-page__journey-images"${imageUrls.length ? '' : ' hidden'}>${imageMarkup}</div>
          <div class="order-progress-page__journey-copy">
            <p class="order-progress-page__journey-date">${escapeHtml(date)}</p>
            <p class="order-progress-page__journey-item-title">${escapeHtml(title)}</p>
          </div>
        </article>
      `;
    }).join('');
  }

  function updateTrackingEmbed(root, status, data) {
    const tracking = root.querySelector('[data-order-progress-tracking]');
    const trackingLink = root.querySelector('[data-order-progress-tracking-link]');
    if (!tracking) {
      return;
    }

    const shouldShow = status === 'shipped';
    tracking.hidden = !shouldShow;

    if (!trackingLink) {
      return;
    }

    const baseHref = 'https://pawtowear.com/apps/17TRACK';
    const orderNo = data && data.order_no ? String(data.order_no).trim().replace(/-\d+$/, '') : '';
    const customerEmail = data && data.customer_email ? String(data.customer_email).trim() : '';

    if (!orderNo) {
      trackingLink.href = baseHref;
      return;
    }

    const url = new URL(baseHref);
    url.searchParams.set('order_no', orderNo);
    if (customerEmail) {
      url.searchParams.set('customer_email', customerEmail);
    }

    trackingLink.href = url.toString();
  }

  function renderPreviewImages(root, imageUrls, fallbackImageUrl, fallbackAlt) {
    const grid = root.querySelector('[data-order-progress-preview-grid]');
    if (!grid) {
      return;
    }

    const validImages = Array.isArray(imageUrls)
      ? imageUrls.filter((imageUrl) => typeof imageUrl === 'string' && imageUrl.trim())
      : [];

    if (validImages.length) {
      grid.innerHTML = validImages.map((imageUrl, index) => {
        const escapedUrl = imageUrl.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
        const alt = `${fallbackAlt} ${index + 1}`.trim();
        const escapedAlt = alt.replace(/&/g, '&amp;').replace(/"/g, '&quot;');

        return `
          <div class="order-progress-page__preview-item">
            <img src="${escapedUrl}" alt="${escapedAlt}" loading="lazy">
          </div>
        `;
      }).join('');
      return;
    }

    if (fallbackImageUrl) {
      const escapedUrl = fallbackImageUrl.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
      const escapedAlt = escapeHtml(fallbackAlt);
      grid.innerHTML = `
        <div class="order-progress-page__preview-item" data-order-progress-preview-fallback>
          <img src="${escapedUrl}" alt="${escapedAlt}" loading="lazy">
        </div>
      `;
      return;
    }

    grid.innerHTML = '';
  }

  function getPreviewImagesByStatus(status, data) {
    if (status === WAITING_STATUS) {
      return data && data.raw_images;
    }

    if (status === PHOTO_CONFIRMATION_STATUS) {
      return data && data.primary_reference_images;
    }

    if (status === 'ready_to_ship') {
      return data && data.finished_product_images;
    }

    if (status === PLATE_MAKING_STATUS) {
      return data && data.confirmed_photos;
    }

    return data && data.candidate_images;
  }

  function updateFormContent(root, config, isCandidateActionable, isPhotoConfirmation) {
    const isActionable = isCandidateActionable || isPhotoConfirmation;
    const showUpload = isCandidateActionable || isPhotoConfirmation;
    const feedback = root.querySelector('[data-order-progress-feedback]');
    const uploadTrigger = root.querySelector('[data-order-progress-upload-trigger]');
    const finePrintNode = root.querySelector('[data-order-progress-actionable-only]');
    const primaryButton = root.querySelector('[data-order-progress-submit="approve"]');
    const secondaryButton = root.querySelector('[data-order-progress-submit="request_edits"]');
    const input = root.querySelector('[data-order-progress-upload-input]');
    const uploadBlock = input ? input.closest('[data-order-progress-form-field]') : null;

    if (feedback) {
      feedback.hidden = !isCandidateActionable;
      feedback.placeholder = config && config.feedbackPlaceholder ? config.feedbackPlaceholder : '';
      feedback.setAttribute('aria-label', config && config.feedbackLabel ? config.feedbackLabel : 'Feedback');
      feedback.disabled = !isCandidateActionable;
    }

    if (uploadBlock) {
      uploadBlock.hidden = !showUpload;
    }

    if (uploadTrigger) {
      uploadTrigger.textContent = config && config.uploadTriggerLabel ? config.uploadTriggerLabel : '';
      uploadTrigger.hidden = !showUpload;
      uploadTrigger.setAttribute('aria-disabled', showUpload ? 'false' : 'true');
    }

    if (finePrintNode) {
      const finePrint = config && config.finePrint ? config.finePrint : '';
      finePrintNode.textContent = finePrint;
      finePrintNode.hidden = !isActionable || !finePrint;
    }

    if (primaryButton) {
      const label = config && config.primaryButtonLabel ? config.primaryButtonLabel : '';
      primaryButton.textContent = label;
      primaryButton.hidden = !isActionable || !label;
      primaryButton.disabled = !isActionable;
    }

    if (secondaryButton) {
      const label = config && config.secondaryButtonLabel ? config.secondaryButtonLabel : '';
      secondaryButton.textContent = label;
      secondaryButton.hidden = !isActionable || !label;
      secondaryButton.disabled = !isActionable;
    }

    const uploadList = root.querySelector('[data-order-progress-upload-list]');
    if (uploadList) {
      uploadList.hidden = !showUpload;
    }

    if (input) {
      input.disabled = !showUpload;
    }
  }

  function getSelectedFiles(root) {
    return Array.isArray(root.__orderProgressFiles) ? root.__orderProgressFiles : [];
  }

  function setSelectedFiles(root, files) {
    root.__orderProgressFiles = files;
  }

  function getCurrentOrderProgressData(root) {
    return root.__orderProgressData && typeof root.__orderProgressData === 'object'
      ? root.__orderProgressData
      : null;
  }

  function setCurrentOrderProgressData(root, data) {
    root.__orderProgressData = data && typeof data === 'object' ? data : null;
  }

  function updatePhotoUploadState(root) {
    if (root.dataset.currentStatus !== PHOTO_CONFIRMATION_STATUS) {
      return;
    }

    const data = getCurrentOrderProgressData(root);
    const requiredCount = Number(data && data.required_selection_count || 0);
    const uploadedCount = getSelectedFiles(root).length;
    const hasReplacements = uploadedCount > 0;
    const confirmButton = root.querySelector('[data-order-progress-submit="approve"]');
    if (confirmButton) {
      confirmButton.disabled = root.dataset.submitting === 'true'
        || requiredCount < 1
        || hasReplacements && uploadedCount !== requiredCount;
    }

    if (!hasReplacements) {
      setStatusMessage(root, '', null);
      return;
    }

    setStatusMessage(
      root,
      `Replacement photos: ${uploadedCount} of ${requiredCount}`,
      uploadedCount === requiredCount ? null : 'error'
    );
  }

  function renderSelectedFiles(root) {
    const files = getSelectedFiles(root);
    const list = root.querySelector('[data-order-progress-upload-list]');
    if (!list) {
      return;
    }

    if (!files.length) {
      list.innerHTML = '';
      return;
    }

    list.innerHTML = files.map((file, index) => {
      const objectUrl = URL.createObjectURL(file);
      return `
        <div class="order-progress-page__upload-item" data-upload-index="${index}">
          <img src="${objectUrl}" alt="${escapeHtml(file.name || `Upload ${index + 1}`)}">
          <button type="button" class="order-progress-page__upload-remove" data-remove-upload="${index}" aria-label="Remove image">
            ×
          </button>
        </div>
      `;
    }).join('');
  }

  function syncInputFiles(root) {
    const input = root.querySelector('[data-order-progress-upload-input]');
    if (!input || typeof DataTransfer === 'undefined') {
      return;
    }

    const transfer = new DataTransfer();
    getSelectedFiles(root).forEach((file) => transfer.items.add(file));
    input.files = transfer.files;
  }

  function setSubmitting(root, isSubmitting) {
    root.dataset.submitting = isSubmitting ? 'true' : 'false';
    root.querySelectorAll('[data-order-progress-submit]').forEach((button) => {
      button.disabled = isSubmitting || button.hidden;
    });

    const trigger = root.querySelector('[data-order-progress-upload-trigger]');
    if (trigger) {
      trigger.setAttribute('aria-disabled', isSubmitting || trigger.hidden ? 'true' : 'false');
    }

    const input = root.querySelector('[data-order-progress-upload-input]');
    if (input) {
      const uploadBlock = input.closest('[data-order-progress-form-field]');
      input.disabled = isSubmitting || !uploadBlock || uploadBlock.hidden;
    }

    const feedback = root.querySelector('[data-order-progress-feedback]');
    if (feedback) {
      feedback.disabled = isSubmitting || feedback.hidden;
    }

  }

  function showDialog(root, options) {
    const dialog = root.querySelector('[data-order-progress-dialog]');
    const title = root.querySelector('[data-order-progress-dialog-title]');
    const body = root.querySelector('[data-order-progress-dialog-body]');
    const confirmButton = root.querySelector('[data-order-progress-dialog-confirm]');
    const cancelButton = root.querySelector('[data-order-progress-dialog-cancel]');
    if (!dialog || !title || !body || !confirmButton) {
      return false;
    }

    const content = options && options.content ? options.content : null;
    if (!content) {
      return false;
    }

    title.textContent = content.title;
    body.textContent = content.body;
    dialog.dataset.redirectOnConfirm = options && options.redirectOnConfirm ? 'true' : 'false';
    dialog.dataset.confirmAction = options && options.confirmAction ? options.confirmAction : '';
    dialog.dataset.cancelable = options && options.cancelable ? 'true' : 'false';
    confirmButton.textContent = options && options.confirmLabel ? options.confirmLabel : 'Got It';
    if (cancelButton) {
      cancelButton.textContent = options && options.cancelLabel ? options.cancelLabel : 'Go Back';
      cancelButton.hidden = dialog.dataset.cancelable !== 'true';
    }
    dialog.hidden = false;
    document.body.style.overflow = 'hidden';

    if (dialog.dataset.cancelable === 'true') {
      window.requestAnimationFrame(() => {
        confirmButton.focus();
      });
    }

    return true;
  }

  function showApiErrorDialog(root, message) {
    const errorMessage = (typeof message === 'string' && message.trim()) ? message : 'Something went wrong. Please try again.';
    showDialog(root, {
      content: {
        title: 'Request failed',
        body: errorMessage
      },
      redirectOnConfirm: false
    });
  }

  function showSuccessDialog(root, decision) {
    const content = SUCCESS_DIALOG_CONTENT[decision] || SUCCESS_DIALOG_CONTENT.request_edits;
    showDialog(root, {
      content,
      redirectOnConfirm: true
    });
  }

  function showValidationDialog(root, decision) {
    const content = VALIDATION_DIALOG_CONTENT[decision];
    if (!content) {
      return;
    }

    showDialog(root, {
      content,
      redirectOnConfirm: false
    });
  }

  function closeDialog(root) {
    const dialog = root.querySelector('[data-order-progress-dialog]');
    if (!dialog) {
      return;
    }

    dialog.hidden = true;
    delete dialog.dataset.redirectOnConfirm;
    delete dialog.dataset.confirmAction;
    delete dialog.dataset.cancelable;
    const cancelButton = dialog.querySelector('[data-order-progress-dialog-cancel]');
    if (cancelButton) {
      cancelButton.hidden = true;
    }
    document.body.style.overflow = '';
  }

  function renderState(root, status, data, options = {}) {
    const config = getStatusConfig(root, status) || getDefaultConfig(root);
    if (!config) {
      setVisibility(root, false);
      return;
    }

    root.dataset.currentStatus = status || config.status;
    root.__orderProgressPreviewMode = Boolean(options.previewMode);

    updateShellMinHeight(root, config);
    updateBackgroundColor(root, config);
    updateTypography(root, config);
    updateTitle(root, config);
    updateSteps(root, config);
    renderBodyContent(root, config);
    renderJourney(root, status, data, config, Boolean(options.previewMode));
    updateTrackingEmbed(root, status, data);
    updateRevisionSummary(root, config, data, Boolean(options.previewMode));
    updateDesignNotes(root, status, data, config, Boolean(options.previewMode));

    const previewImages = options.previewMode ? [] : getPreviewImagesByStatus(status, data);
    renderPreviewImages(root, previewImages, config.previewImageUrl, config.title || 'Order progress image');

    const isCandidateActionable = status === ACTIONABLE_STATUS && config.showFeedbackForm;
    const isPhotoConfirmation = status === PHOTO_CONFIRMATION_STATUS;
    updateFormContent(root, config, isCandidateActionable, isPhotoConfirmation);
    setSubmitting(root, false);
    if (isPhotoConfirmation) {
      updatePhotoUploadState(root);
    } else {
      setStatusMessage(root, '', null);
    }
    setVisibility(root, true);
  }

  async function submitFeedback(root, decision) {
    if (root.__orderProgressPreviewMode) {
      setStatusMessage(root, EDITOR_PREVIEW_MESSAGES.submissionDisabled, null);
      return;
    }

    const token = getTokenFromContext(root);
    if (!token) {
      setStatusMessage(root, 'Missing order token.', 'error');
      return;
    }

    const formData = new FormData();
    formData.set('urlHash', token);
    formData.set('decision', decision);

    const feedback = root.querySelector('[data-order-progress-feedback]');
    const comment = feedback ? String(feedback.value || '').trim() : '';
    const currentData = getCurrentOrderProgressData(root);
    if (decision === 'request_edits' && !comment) {
      setStatusMessage(root, '', null);
      showValidationDialog(root, decision);
      if (feedback) {
        feedback.focus();
      }
      return;
    }

    if (comment) {
      formData.set('comment', comment);
    }

    getSelectedFiles(root).forEach((file) => {
      formData.append('images', file);
    });

    if (decision === 'approve') {
      const candidateImageUrls = Array.isArray(currentData && currentData.candidate_images)
        ? currentData.candidate_images.filter((imageUrl) => typeof imageUrl === 'string' && imageUrl.trim())
        : [];

      if (!candidateImageUrls.length) {
        setStatusMessage(root, 'Missing candidate images for approval.', 'error');
        return;
      }

      candidateImageUrls.forEach((imageUrl) => {
        formData.append('candidateImageUrls', imageUrl);
      });
    }

    setSubmitLoading(root, true);
    setSubmitting(root, true);
    setStatusMessage(root, 'Submitting your feedback...', null);

    const response = await fetchJson(root, {
      endpoint: FEEDBACK_ENDPOINT,
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    });

    if (!response.success) {
      setSubmitLoading(root, false);
      setSubmitting(root, false);
      setStatusMessage(root, response.error || 'Failed to submit feedback.', 'error');
      showApiErrorDialog(root, response.error || 'Failed to submit feedback.');
      return;
    }

    if (decision === 'request_edits' && comment) {
      persistRevisionNote(root, comment);
    }

    if (decision === 'approve') {
      persistRevisionNote(root, '');
    }

    setStatusMessage(root, '', null);
    setSelectedFiles(root, []);
    renderSelectedFiles(root);
    syncInputFiles(root);
    if (feedback) {
      feedback.value = '';
    }

    setSubmitLoading(root, false);
    setSubmitting(root, false);
    showSuccessDialog(root, decision);
  }

  async function submitPhotoSelection(root, decision) {
    if (root.__orderProgressPreviewMode) {
      setStatusMessage(root, EDITOR_PREVIEW_MESSAGES.submissionDisabled, null);
      return;
    }

    const token = getTokenFromContext(root);
    const data = getCurrentOrderProgressData(root);
    const requestEventId = data && typeof data.request_event_id === 'string'
      ? data.request_event_id.trim()
      : '';
    const requiredCount = Number(data && data.required_selection_count || 0);
    const replacementFiles = getSelectedFiles(root);

    if (!token || !requestEventId) {
      setStatusMessage(root, 'Missing photo confirmation information.', 'error');
      return;
    }
    if (decision === 'reject' && (requiredCount < 1 || replacementFiles.length !== requiredCount)) {
      setStatusMessage(root, `Please upload exactly ${requiredCount} replacement photos.`, 'error');
      return;
    }

    const formData = new FormData();
    formData.set('urlHash', token);
    formData.set('requestEventId', requestEventId);
    formData.set('decision', decision);
    if (decision === 'reject') {
      replacementFiles.forEach((file) => formData.append('images', file));
    }

    setSubmitLoading(root, true);
    setSubmitting(root, true);
    setStatusMessage(root, decision === 'approve' ? 'Approving your photos...' : 'Uploading replacement photos...', null);

    const response = await submitPhotoSelectionRequest(root, formData);

    if (!response.success) {
      setSubmitLoading(root, false);
      setSubmitting(root, false);
      updatePhotoUploadState(root);
      setStatusMessage(root, response.error || 'Failed to confirm photos.', 'error');
      showApiErrorDialog(root, response.error || 'Failed to confirm photos.');
      return;
    }

    setStatusMessage(root, '', null);
    showSuccessDialog(root, 'confirm_photos');
  }

  function bindUploadEvents(root) {
    const input = root.querySelector('[data-order-progress-upload-input]');
    const list = root.querySelector('[data-order-progress-upload-list]');
    if (!input || !list) {
      return;
    }

    input.addEventListener('change', () => {
      const nextFiles = Array.from(input.files || []).filter((file) => file && file.size > 0);
      setSelectedFiles(root, nextFiles);
      renderSelectedFiles(root);
      syncInputFiles(root);
      updatePhotoUploadState(root);
    });

    list.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }

      const removeIndex = Number(target.dataset.removeUpload || '-1');
      if (removeIndex < 0) {
        return;
      }

      const nextFiles = getSelectedFiles(root).filter((_, index) => index !== removeIndex);
      setSelectedFiles(root, nextFiles);
      renderSelectedFiles(root);
      syncInputFiles(root);
      updatePhotoUploadState(root);
    });
  }

  function bindSubmitEvents(root) {
    root.querySelectorAll('[data-order-progress-submit]').forEach((button) => {
      button.addEventListener('click', () => {
        const decision = button.dataset.orderProgressSubmit;
        if (decision !== 'approve' && decision !== 'request_edits') return;

        if (root.dataset.currentStatus === PHOTO_CONFIRMATION_STATUS) {
          if (decision !== 'approve') return;
          const hasReplacements = getSelectedFiles(root).length > 0;
          void submitPhotoSelection(root, hasReplacements ? 'reject' : 'approve');
          return;
        }

        if (decision === 'approve') {
          const dialogShown = showDialog(root, {
            content: APPROVE_CONFIRM_DIALOG_CONTENT,
            confirmAction: 'approve',
            confirmLabel: 'Yes, Approve Design',
            cancelable: true,
            cancelLabel: 'Go Back'
          });
          if (dialogShown) return;
        }

        void submitFeedback(root, decision);
      });
    });
  }

  function bindDialogEvents(root) {
    const dialog = root.querySelector('[data-order-progress-dialog]');
    const confirmButton = root.querySelector('[data-order-progress-dialog-confirm]');
    const cancelButton = root.querySelector('[data-order-progress-dialog-cancel]');
    const backdrop = root.querySelector('[data-order-progress-dialog-backdrop]');
    if (!dialog || !confirmButton) {
      return;
    }

    confirmButton.addEventListener('click', () => {
      const shouldRedirect = dialog && dialog.dataset.redirectOnConfirm === 'true';
      const confirmAction = dialog.dataset.confirmAction;
      closeDialog(root);
      if (confirmAction === 'approve') {
        void submitFeedback(root, 'approve');
        return;
      }
      if (shouldRedirect) {
        window.location.reload();
      }
    });

    const closeIfCancelable = () => {
      if (dialog.dataset.cancelable === 'true') {
        closeDialog(root);
      }
    };

    if (cancelButton) {
      cancelButton.addEventListener('click', closeIfCancelable);
    }

    if (backdrop) {
      backdrop.addEventListener('click', closeIfCancelable);
    }
  }

  function bindRoot(root) {
    if (root.dataset.orderProgressBound === 'true') {
      return;
    }

    root.dataset.orderProgressBound = 'true';
    setSelectedFiles(root, []);
    bindUploadEvents(root);
    bindSubmitEvents(root);
    bindDialogEvents(root);
  }

  function getEditorPreviewStatus(root) {
    const selectedStatus = typeof root.dataset.editorSelectedStatus === 'string'
      ? root.dataset.editorSelectedStatus.trim()
      : '';
    if (selectedStatus) {
      return selectedStatus;
    }

    const previewStatus = typeof root.dataset.previewStatus === 'string' ? root.dataset.previewStatus.trim() : '';
    if (previewStatus) {
      return previewStatus;
    }

    const defaultConfig = getDefaultConfig(root);
    return defaultConfig ? defaultConfig.status : '';
  }

  async function initRoot(root) {
    bindRoot(root);

    const editor = isDesignMode();
    const token = getTokenFromContext(root);
    const previewStatus = getEditorPreviewStatus(root);
    const forcePreview = editor && previewStatus;

    if (forcePreview) {
      if (previewStatus === LOADING_PREVIEW_STATUS) {
        setCurrentOrderProgressData(root, null);
        setStatusMessage(root, '', null);
        showLoadingPreview(root);
        return;
      }

      setLoading(root, false);
      setCurrentOrderProgressData(root, null);
      renderState(root, previewStatus, null, { previewMode: true });
      setStatusMessage(root, '', null);
      return;
    }

    if (editor && !token && previewStatus) {
      if (previewStatus === LOADING_PREVIEW_STATUS) {
        setCurrentOrderProgressData(root, null);
        setStatusMessage(root, '', null);
        showLoadingPreview(root);
        return;
      }

      setLoading(root, false);
      setCurrentOrderProgressData(root, null);
      renderState(root, previewStatus, null, { previewMode: true });
      setStatusMessage(root, EDITOR_PREVIEW_MESSAGES.noToken, null);
      return;
    }

    const response = await fetchOrderProgress(root);
    setLoading(root, false);

    if (!response.success) {
      setCurrentOrderProgressData(root, null);
      if (editor && previewStatus) {
        renderState(root, previewStatus, null, { previewMode: true });
        setStatusMessage(root, EDITOR_PREVIEW_MESSAGES.noToken, null);
        return;
      }

      setVisibility(root, false);
      setStatusMessage(root, response.error || 'Failed to load order progress.', 'error');
      return;
    }

    const data = getPayloadData(response.data || {});
    const status = typeof data.status === 'string' ? data.status.trim() : '';
    if (!getVisibleStatuses(root).has(status)) {
      setVisibility(root, false);
      return;
    }

    setCurrentOrderProgressData(root, data);
    renderState(root, status, data, { previewMode: false });
    setStatusMessage(root, '', null);
  }

  function init(context) {
    const scope = context && context.querySelectorAll ? context : document;
    const roots = scope.matches && scope.matches(ROOT_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll(ROOT_SELECTOR));

    if (!roots.length) {
      return;
    }

    roots.forEach((root) => {
      if (!root.dataset.apiBaseUrl) {
        return;
      }

      void initRoot(root);
    });
  }

  function bindGlobalEvents() {
    if (window[GLOBAL_FLAG]) {
      return;
    }

    window[GLOBAL_FLAG] = true;
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target instanceof HTMLElement) {
        init(event.target);
      }
    });
    document.addEventListener('shopify:section:select', (event) => {
      if (event.target instanceof HTMLElement) {
        init(event.target);
      }
    });
    document.addEventListener('shopify:block:select', (event) => {
      if (!(event.target instanceof HTMLElement)) {
        return;
      }

      const marker = event.target.closest('[data-order-progress-editor-block]');
      if (!marker) {
        return;
      }

      const root = marker.closest(ROOT_SELECTOR);
      if (!(root instanceof HTMLElement)) {
        return;
      }

      root.dataset.editorSelectedStatus = marker.dataset.orderProgressEditorStatus || '';
      init(root);
    });
    document.addEventListener('shopify:block:deselect', (event) => {
      if (!(event.target instanceof HTMLElement)) {
        return;
      }

      const marker = event.target.closest('[data-order-progress-editor-block]');
      if (!marker) {
        return;
      }

      const root = marker.closest(ROOT_SELECTOR);
      if (!(root instanceof HTMLElement)) {
        return;
      }

      delete root.dataset.editorSelectedStatus;
      init(root);
    });
  }

  window.OrderProgressApi = window.OrderProgressApi || {
    fetch: fetchOrderProgress,
    resolveEndpoint,
    progressEndpoint: PROGRESS_ENDPOINT,
    feedbackEndpoint: FEEDBACK_ENDPOINT,
    photoSelectionEndpoint: PHOTO_SELECTION_ENDPOINT
  };

  bindGlobalEvents();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
})();
